import { FETCH_QUERIES_NAME, MUTATION_KEYS } from '@common/constants';
import { AxiosErrorResponse } from '@core/error.models';
import { QueryKey, useMutation, useQuery } from '@tanstack/react-query';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { IResultResponse } from '@common/api.models';
import {
  apiRoutes,
  IUploadProjectFileAsyncConfigProps,
  IUploadProjectFileAsyncProps,
  uploadProjectFileDrawingAsync,
  uploadProjectFileAttachmentAsync,
  getAttachmentAsync,
  deleteProjectFileAsync,
  uploadProjectFileDiscussionAttachmentAsync,
  uploadProjectFileDrawingTempAsync,
} from './projectFile.services';
import {
  IDeleteProjectFileRequest,
  IProjectFileAttachmentChunkCompleteRequest,
  IProjectFileAttachmentsDesignBriefsRequest,
  IProjectFileAttachmentsDesignModificationsRequest,
  IProjectFileAttachmentsLoadingRemovalCertsRequest,
  IProjectFileAttachmentsMasterSchedulesRequest,
  IProjectFileAttachmentsTransmittalsRequest,
  IProjectFileAttachmentsTwcGeRequestsRequest,
  IProjectFileBase,
  IProjectFileDiscussionAttachmentChunkCompleteRequest,
  IProjectFileDrawingChunkCompleteRequest,
  IProjectFileProcessResponse,
} from './projectFile.models';

export const useUploadProjectFileDrawingTempAsync = (configProps: IUploadProjectFileAsyncConfigProps) => {
  return useMutation<IProjectFileProcessResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPLOAD_PROJECT_FILE_DRAWING],
    async (uploadProjectFileProps: IUploadProjectFileAsyncProps<IProjectFileDrawingChunkCompleteRequest>) => {
      const result = await uploadProjectFileDrawingTempAsync(uploadProjectFileProps, configProps);
      return result;
    },
  );
};

export const useUploadProjectFileDrawingAsync = (configProps: IUploadProjectFileAsyncConfigProps) => {
  return useMutation<IProjectFileProcessResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPLOAD_PROJECT_FILE_DRAWING],
    async (uploadProjectFileProps: IUploadProjectFileAsyncProps<IProjectFileDrawingChunkCompleteRequest>) => {
      const result = await uploadProjectFileDrawingAsync(uploadProjectFileProps, configProps);
      return result;
    },
  );
};

export const useUploadProjectFileAttachmentAsync = (configProps: IUploadProjectFileAsyncConfigProps) => {
  return useMutation<IProjectFileProcessResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPLOAD_PROJECT_FILE_ATTACHMENT],
    async (uploadProjectFileProps: IUploadProjectFileAsyncProps<IProjectFileAttachmentChunkCompleteRequest>) => {
      const result = await uploadProjectFileAttachmentAsync(uploadProjectFileProps, configProps);
      return result;
    },
  );
};

export const useUploadProjectFileDiscussionAttachmentAsync = (configProps: IUploadProjectFileAsyncConfigProps) => {
  return useMutation<IProjectFileProcessResponse, AxiosErrorResponse, any, unknown>(
    [MUTATION_KEYS.UPLOAD_PROJECT_FILE_DISCUSSION_ATTACHMENT],
    async (
      uploadProjectFileProps: IUploadProjectFileAsyncProps<IProjectFileDiscussionAttachmentChunkCompleteRequest>,
    ) => {
      const result = await uploadProjectFileDiscussionAttachmentAsync(uploadProjectFileProps, configProps);
      return result;
    },
  );
};

const useFetchAttachment = <T>(queryKey: QueryKey, apiRouteName: string, props: T, enabled = true) => {
  return useQuery<IProjectFileBase[] | undefined, AxiosErrorResponse>(
    queryKey,
    async () => {
      const results = await getAttachmentAsync<T>(apiRouteName, props);
      return results.items;
    },
    {
      enabled,
    },
  );
};

export const useFetchAttachmentTransmittal = (props: IProjectFileAttachmentsTransmittalsRequest, enabled = true) => {
  return useFetchAttachment<IProjectFileAttachmentsTransmittalsRequest>(
    [props.projectId, props.transmittalId, FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_TRANSMITTALS, props.fileType],
    apiRoutes.attachmentTransmittals,
    props,
    isPositiveNumber(props.transmittalId) && enabled,
  );
};
export const useFetchAttachmentMasterSchedule = (
  props: IProjectFileAttachmentsMasterSchedulesRequest,
  enabled = true,
) => {
  return useFetchAttachment<IProjectFileAttachmentsMasterSchedulesRequest>(
    [
      props.projectId,
      props.projectMasterScheduleId,
      FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_MASTER_SCHEDULES,
      props.fileType,
    ],
    apiRoutes.attachmentMasterSchedules,
    props,
    enabled && isPositiveNumber(props.projectMasterScheduleId),
  );
};

export const useFetchAttachmentDesignBrief = (props: IProjectFileAttachmentsDesignBriefsRequest, enabled = true) => {
  return useFetchAttachment<IProjectFileAttachmentsDesignBriefsRequest>(
    [props.projectId, props.designBriefId, FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_BRIEFS, props.fileType],
    apiRoutes.attachmentDesignBriefs,
    props,
    enabled && isPositiveNumber(props.designBriefId),
  );
};

export const useFetchAttachmentDesignModification = (
  props: IProjectFileAttachmentsDesignModificationsRequest,
  enabled = true,
) => {
  return useFetchAttachment<IProjectFileAttachmentsDesignModificationsRequest>(
    [
      props.projectId,
      props.designModificationId,
      FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_DESIGN_MODIFICATIONS,
      props.fileType,
    ],
    apiRoutes.attachmentDesignModifications,
    props,
    enabled && isPositiveNumber(props.designModificationId),
  );
};

export const useFetchAttachmentLoadingRemovalCerts = (
  props: IProjectFileAttachmentsLoadingRemovalCertsRequest,
  enabled = true,
) => {
  return useFetchAttachment<IProjectFileAttachmentsLoadingRemovalCertsRequest>(
    [
      props.projectId,
      props.loadingRemovalCertId,
      FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_LOADING_REMOVAL_CERTS,
      props.fileType,
    ],
    apiRoutes.attachmentLoadingRemovalCerts,
    props,
    isPositiveNumber(props.loadingRemovalCertId) && enabled,
  );
};

export const useFetchAttachmentTwcGeRequests = (props: IProjectFileAttachmentsTwcGeRequestsRequest, enabled = true) => {
  return useFetchAttachment<IProjectFileAttachmentsTwcGeRequestsRequest>(
    [
      props.projectId,
      props.projectTwcGeRequestId,
      FETCH_QUERIES_NAME.PROJECT_FILE_ATTACHMENTS_TWC_GE_REQUESTS,
      props.fileType,
    ],
    apiRoutes.attachmentTwcGeRequests,
    props,
    isPositiveNumber(props.projectTwcGeRequestId) && enabled,
  );
};

export const useDeleteProjectFileAsync = () => {
  return useMutation<IResultResponse, AxiosErrorResponse, IDeleteProjectFileRequest, unknown>(
    [MUTATION_KEYS.DELETE_PROJECT_FILE],
    async (request: IDeleteProjectFileRequest) => {
      const result = await deleteProjectFileAsync(request);
      return result;
    },
  );
};
